import React, { useEffect, useMemo, useState } from "react";

import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";

import {
  FilterSection,
  SearchBar,
  GetLinksModal,
} from "../../../OrganizationQuickTales/components/components";

import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import {
  drawerWidth,
  mainLayoutStyles,
  showNotification,
} from "../../../../constants";
import { AddQuickTalesToOrgTable } from "../../components/components";
import { useAuth } from "../../../../hooks";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import {
  deleteQuickTale,
  getFilterQuickTales,
  quickTalesSearch,
  updateQuickTale,
  getSingleUser,
} from "../../../OrganizationQuickTales/hooks/QuickTalesHooks";
import { Toast } from "../../../../common/alert";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import FilterModalComponent from "../../../../common/FilterModalComponent";
import { getDateRange } from "../../../OrgProf/hook/Organizationhook";
import { functions } from "../../../../firebase";
import { useGetEmailTemplates } from "../../../OrganizationEmailTemplates/hooks/EmailTemplatesHooks";
import {
  getCustomNarratorByName,
  updateCustomNarrator,
} from "../../../OrganizationQuickTales/hooks/CustomNarratorsHooks";
import { getOrgAreas } from "../../../AreaManagement/hooks/AreaHook";
console.log('Add QuickTalesToOrganizationContainer loaded');

const AddQuickTalesToOrganizationContainer = () => {
  const { currentUser, organizationId } = useAuth();
  const { handleGetAllQuickTales, setLoading, loading, pendingQuickTales } =
    useQuickTalesContext();
  const [quictalesInOrg, setQuictalesInOrg] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [selected, setSelected] = useState([]);
  // const [filterOptions, setFilterOptions] = useState([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [whoCanListenFilterOptions, setWhoCanListenFilterOptions] = useState(
    []
  );
  const [modifiedStatusDateFilterOptions, setModifiedStatusDateFilterOptions] =
    useState([]);

  // Org areas — used to resolve the Areas column (display-only).
  const [orgAreas, setOrgAreas] = useState([]);


  // Email template + narrator state for Send Link flow
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailPromptModalOpen, setEmailPromptModalOpen] = useState(false);
  const [narratorEmail, setNarratorEmail] = useState("");
  const [narratorFirstName, setNarratorFirstName] = useState("");
  const [narratorLastName, setNarratorLastName] = useState("");
  const [narratorsWithoutEmails, setNarratorsWithoutEmails] = useState([]);
  const [currentNarratorIndex, setCurrentNarratorIndex] = useState(0);

  // Missing Narrator Check States
  const [missingNarratorQueue, setMissingNarratorQueue] = useState([]);
  const [validQuickTalesForSend, setValidQuickTalesForSend] = useState([]);
  const [pendingTemplateId, setPendingTemplateId] = useState("");
  const [showMissingNarratorModal, setShowMissingNarratorModal] = useState(false);

  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState([]);

  // Get Link template selection states
  const [getLinkTemplateSelectionModalOpen, setGetLinkTemplateSelectionModalOpen] = useState(false);
  const [selectedGetLinkTemplate, setSelectedGetLinkTemplate] = useState("");

  const { templates: allEmailTemplates } = useGetEmailTemplates(
    organizationId || currentUser.organization
  );
  const activeEmailTemplates =
    allEmailTemplates?.filter((t) => t.status === "active") || [];

  useEffect(() => {
    handleGetAllQuickTales();
    // if (pendingQuickTales?.length) {
    // } else {
    //   handleGetAllQuickTales();
    // }
  }, []);

  useEffect(() => {
    setQuictalesInOrg(pendingQuickTales);
  }, [pendingQuickTales]);

  // Fetch org areas once so we can resolve the Areas column for each QuickTale.
  useEffect(() => {
    const orgId = organizationId ? organizationId : currentUser?.organization;
    if (!orgId) return;

    const fetchAreas = async () => {
      try {
        const snapshot = await getOrgAreas(orgId);
        const data = (snapshot?.docs || []).map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setOrgAreas(data);
      } catch (error) {
        console.log("error fetching org areas:", error.message);
        setOrgAreas([]);
      }
    };

    fetchAreas();
  }, [organizationId, currentUser?.organization]);

  // Build a map of quicktale id -> comma-separated area names based on
  // area.albumsAndQTs membership (reserved "All"/"Nearby" areas excluded).
  const areaNamesByQuickTaleId = useMemo(() => {
    const map = new Map();
    (orgAreas || []).forEach((area) => {
      const areaName = area?.name;
      if (!areaName) return;
      const lowerName = String(areaName).trim().toLowerCase();
      if (lowerName === "all" || lowerName === "nearby") return;

      (area?.albumsAndQTs || []).forEach((entry) => {
        if (entry?.type === "quicktales" && entry?.id) {
          const existing = map.get(entry.id) || [];
          existing.push(areaName);
          map.set(entry.id, existing);
        }
      });
    });
    return map;
  }, [orgAreas]);

  // Enrich the displayed QuickTales with a resolved Areas string.
  const quicktalesWithAreas = useMemo(() => {
    return (quictalesInOrg || []).map((qt) => ({
      ...qt,
      areaNameResolved: (areaNamesByQuickTaleId.get(qt.id) || []).join(", "),
    }));
  }, [quictalesInOrg, areaNamesByQuickTaleId]);


  // Auto-select default or single active template when template modal opens
  useEffect(() => {
    if (!templateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedTemplate) return; // Don't override user choice

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }
  }, [templateSelectionModalOpen, activeEmailTemplates, selectedTemplate]);

  // Auto-select default or single active template when Get Link template modal opens
  useEffect(() => {
    if (!getLinkTemplateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedGetLinkTemplate) return;

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedGetLinkTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }
  }, [
    getLinkTemplateSelectionModalOpen,
    activeEmailTemplates,
    selectedGetLinkTemplate,
  ]);

  const handleFilterModalOpen = () => {
    setFilterModal(true);
  };
  const handleFilterModalClose = () => {
    setFilterModal(false);
  };
  const handleDeleteModalOpen = () => {
    setDeleteModal(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
  };
  const handlePageLoadingClose = () => {
    setLoading(false);
  };

  const handleDeleteQuickTale = async () => {
    try {
      setLoading(true);

      selected.forEach(async (item) => {
        let responseTemp = await deleteQuickTale(item);
        console.log("delete quicktale response:", responseTemp);
      });
      await handleGetAllQuickTales();
      setSelected([]);
      // Toast("success", "QuickTale deleted successfully.");
      showNotification("QuickTale deleted successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error deleting quicktale", error.message);
    } finally {
      setLoading(false);
      handleDeleteModalClose();
    }
  };
  const handleUpdateQuickTaleStatus = async (status) => {
    try {
      setLoading(true);
      let dataTemp = {
        // status,
        updatedAt: new Date(),
        approvalStatus: {
          status: status,
          updatedAt: new Date(),
          updatedBy: currentUser?.userName,
        },
      };
      selected.forEach(async (item) => {
        let responseTemp = await updateQuickTale(item, dataTemp);
        console.log("update quicktale response:", responseTemp);
      });
      await handleGetAllQuickTales();
      setSelected([]);

      // Toast("success", "QuickTale updated successfully.");
      showNotification("QuickTale updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuickTaleApprovalStatus = async (status) => {
    try {
      setLoading(true);
      let dataTemp = {
        status,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      };
      selected.forEach(async (item) => {
        let responseTemp = await updateQuickTale(item, dataTemp);
        console.log("update quicktale response:", responseTemp);
      });
      await handleGetAllQuickTales();
      setSelected([]);
      // Toast("success", "QuickTale updated successfully.");
      showNotification("QuickTale updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterQuickTales = async () => {
    let modifiedDateRange;
    if (
      modifiedStatusDateFilterOptions &&
      modifiedStatusDateFilterOptions.length > 0
    ) {
      modifiedDateRange = getDateRange(
        modifiedStatusDateFilterOptions,
        "Modified Status Date"
      );
    }
    setSelected([]);
    if (
      whoCanListenFilterOptions.length > 0 ||
      statusFilterOptions.length > 0 ||
      modifiedStatusDateFilterOptions.length > 0
    ) {
      let temp = await getFilterQuickTales(
        whoCanListenFilterOptions,
        statusFilterOptions,
        // modifiedStatusDateFilterOptions,
        modifiedDateRange,
        setLoading,
        handleFilterModalClose,
        organizationId ? organizationId : currentUser.organization,
        "pending"
      );
      console.log("filter data:", temp);
      setQuictalesInOrg(temp);
    }
    if (
      whoCanListenFilterOptions.length === 0 &&
      statusFilterOptions.length === 0 &&
      modifiedStatusDateFilterOptions.length === 0
    ) {
      setQuictalesInOrg(pendingQuickTales);
    }
  };

  const handleSearchQuickTales = async (e) => {
    try {
      let value = e.target.value;
      if (value.length >= 1) {
        let quicktalesSearchTemp = await quickTalesSearch(
          pendingQuickTales,
          value
        );
        setQuictalesInOrg(quicktalesSearchTemp);
      } else {
        setQuictalesInOrg(pendingQuickTales);
      }
    } catch (error) {
      console.log("error searching quicktales:", error.message);
    }
  };

  const handleDeleteChip = (filterType, filterItem) => {
    if (filterType === "status") {
      setStatusFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    } else if (filterType === "whoCanListen") {
      setWhoCanListenFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    } else if (filterType === "modifiedStatusDate") {
      setModifiedStatusDateFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    }
  };

  useEffect(() => {
    handleFilterQuickTales();
  }, [
    statusFilterOptions,
    whoCanListenFilterOptions,
    modifiedStatusDateFilterOptions,
  ]);

  const resetFilter = async () => {
    setWhoCanListenFilterOptions([]);
    setModifiedStatusDateFilterOptions([]);
    setStatusFilterOptions([]);
    setQuictalesInOrg(pendingQuickTales);
    setSelected([]);
    handleFilterModalClose();
  };

  // Generate links for selected QuickTales (quicktales collection)
  const handleGetLink = async () => {
    if (!selected.length) {
      showNotification("Please select at least one QuickTale.");
      return;
    }

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create an email template");
      return;
    }

    if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }

    setGetLinkTemplateSelectionModalOpen(true);
  };

  const handleGetLinkTemplateSelectionContinue = async () => {
    if (!selectedGetLinkTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setGetLinkTemplateSelectionModalOpen(false);
    setLoading(true);

    try {
      const generateExternalUpdateLink = httpsCallable(
        functions,
        "generateExternalUpdateLink"
      );
      const links = await Promise.all(
        selected.map(async (quicktaleId) => {
          const result = await generateExternalUpdateLink({
            quicktaleId,
            origin: window.location.origin,
            collection: "quicktales",
            emailTemplateId: selectedGetLinkTemplate,
          });
          const quicktale = quictalesInOrg.find((qt) => qt.id === quicktaleId);
          return {
            link: result.data.link,
            title: quicktale?.title || "Untitled",
          };
        })
      );

      if (links.length > 0) {
        setGeneratedLinks(links);
        setGetLinkModalOpen(true);
      } else {
        showNotification("No links could be generated");
      }
    } catch (error) {
      Toast("error", `Error generating links: ${error.message}`);
      console.error("Error calling generateExternalUpdateLink:", error);
    } finally {
      setLoading(false);
      setSelectedGetLinkTemplate("");
    }
  };

  const handleGetLinkTemplateSelectionClose = () => {
    setGetLinkTemplateSelectionModalOpen(false);
    setSelectedGetLinkTemplate("");
  };

  // Entry point for Send Link from Submitted QuickTales
  const handleSendLink = () => {
    if (!selected.length) {
      showNotification("Please select at least one QuickTale.");
      return;
    }

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification(
        "Please create at least one active email template before sending links."
      );
      return;
    }

    if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }

    setTemplateSelectionModalOpen(true);
  };

  const handleTemplateSelectionContinue = async () => {
    if (!selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setTemplateSelectionModalOpen(false);
    
    // Start Missing Narrator Check
    const selectedQuickTales = quictalesInOrg.filter((qt) => selected.includes(qt.id));
    const missingNarratorQTs = selectedQuickTales.filter(qt => !qt.narrator || qt.narrator.trim() === "");
    const validQTs = selectedQuickTales.filter(qt => qt.narrator && qt.narrator.trim() !== "");

    if (missingNarratorQTs.length > 0) {
      setMissingNarratorQueue(missingNarratorQTs);
      setValidQuickTalesForSend(validQTs);
      setPendingTemplateId(selectedTemplate);
      setShowMissingNarratorModal(true);
    } else {
      await processSendLinksWithTemplate(selectedTemplate);
    }
  };

  const handleMissingNarratorContinue = async () => {
    // Skip the current QuickTale (first in queue)
    const remainingQueue = missingNarratorQueue.slice(1);
    
    if (remainingQueue.length > 0) {
      setMissingNarratorQueue(remainingQueue);
    } else {
      setShowMissingNarratorModal(false);
      // Proceed with the valid QuickTales we collected earlier
      await processSendLinksWithTemplate(pendingTemplateId, validQuickTalesForSend);
      
      // Reset states
      setMissingNarratorQueue([]);
      setValidQuickTalesForSend([]);
      setPendingTemplateId("");
    }
  };

  const handleMissingNarratorCancel = () => {
    setShowMissingNarratorModal(false);
    setMissingNarratorQueue([]);
    setValidQuickTalesForSend([]);
    setPendingTemplateId("");
    setSelectedTemplate("");
  };

  const handleTemplateSelectionClose = () => {
    setTemplateSelectionModalOpen(false);
    setSelectedTemplate("");
  };

  const processSendLinksWithTemplate = async (templateId, customQuickTales = null) => {
    setLoading(true);
    const narratorsToEmail = [];
    const narratorsMissingEmails = [];

    // Use customQuickTales if provided (from missing narrator flow), otherwise filter from selected IDs
    const selectedQuickTales = customQuickTales || quictalesInOrg.filter((qt) =>
      selected.includes(qt.id)
    );
    
    const groupedByNarrator = selectedQuickTales.reduce((acc, qt) => {
      const narratorKey = qt.narrator;
      if (!narratorKey) return acc;
      if (!acc[narratorKey]) {
        acc[narratorKey] = [];
      }
      acc[narratorKey].push(qt);
      return acc;
    }, {});

    for (const narratorName in groupedByNarrator) {
      const quicktalesForNarrator = groupedByNarrator[narratorName];
      let narratorData;

      try {
        narratorData = await getSingleUser(narratorName);
      } catch (e) {
        // likely a custom narrator
      }

      if (narratorData && narratorData.exists() && narratorData.data().email) {
        const userData = narratorData.data();
        narratorsToEmail.push({
          narratorEmailAddress: userData.email,
          quicktales: quicktalesForNarrator,
          firstName: userData.firstName,
          lastName: userData.lastName,
        });
      } else {
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        const customNarrator = await getCustomNarratorByName(
          narratorName,
          userIdToUse
        );

        const hasEmail = !!customNarrator?.email;
        const hasFirstName = !!customNarrator?.firstName;
        const hasLastName = !!customNarrator?.lastName;

        if (hasEmail && hasFirstName && hasLastName) {
          narratorsToEmail.push({
            narratorEmailAddress: customNarrator.email,
            quicktales: quicktalesForNarrator,
            firstName: customNarrator.firstName,
            lastName: customNarrator.lastName,
          });
        } else {
          narratorsMissingEmails.push({
            customNarrator,
            quicktales: quicktalesForNarrator,
          });
        }
      }
    }

    if (narratorsMissingEmails.length > 0) {
      setNarratorsWithoutEmails(narratorsMissingEmails);
      setCurrentNarratorIndex(0);
      const first = narratorsMissingEmails[0];
      setNarratorEmail(first.customNarrator?.email || "");
      setNarratorFirstName(first.customNarrator?.firstName || "");
      setNarratorLastName(first.customNarrator?.lastName || "");
      setEmailPromptModalOpen(true);
      setLoading(false);
    } else {
      await sendEmails(narratorsToEmail, templateId);
      setLoading(false);
    }
  };

  const sendEmails = async (narratorsToEmail, templateId) => {
    try {
      for (const { narratorEmailAddress, quicktales, firstName, lastName } of narratorsToEmail) {
        if (quicktales.length === 1) {
          const sendExternalUpdateLink = httpsCallable(
            functions,
            "sendExternalUpdateLink"
          );
          await sendExternalUpdateLink({
            quicktaleId: quicktales[0].id,
            email: narratorEmailAddress,
            firstName,
            lastName,
            quickTale: { title: quicktales[0].title },
            origin: window.location.origin,
            emailTemplateId: templateId,
            collection: "quicktales",
          });
        } else {
          const sendBulkExternalUpdateLink = httpsCallable(
            functions,
            "sendBulkExternalUpdateLink"
          );
          await sendBulkExternalUpdateLink({
            quickTales: quicktales,
            email: narratorEmailAddress,
            firstName,
            lastName,
            origin: window.location.origin,
            emailTemplateId: templateId,
            collection: "quicktales",
          });
        }
      }
      showNotification("Update link(s) sent successfully.");
    } catch (error) {
      Toast("error", `Error sending link: ${error.message}`);
      console.error(
        "Error sending links from AddQuickTalesToOrganization:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEmailPromptContinue = async () => {
    if (!narratorFirstName) {
      showNotification("Please enter a first name.");
      return;
    }
    if (!narratorEmail) {
      showNotification("Please enter an email address.");
      return;
    }

    const currentNarrator = narratorsWithoutEmails[currentNarratorIndex];

    try {
      if (currentNarrator.customNarrator && currentNarrator.customNarrator.id) {
        await updateCustomNarrator(currentNarrator.customNarrator.id, {
          email: narratorEmail,
          firstName: narratorFirstName,
          lastName: narratorLastName,
        });
      } else {
        throw new Error("Custom narrator document not found.");
      }

      if (currentNarratorIndex < narratorsWithoutEmails.length - 1) {
        const nextIndex = currentNarratorIndex + 1;
        const next = narratorsWithoutEmails[nextIndex];
        setCurrentNarratorIndex(nextIndex);
        setNarratorEmail(next.customNarrator?.email || "");
        setNarratorFirstName(next.customNarrator?.firstName || "");
        setNarratorLastName(next.customNarrator?.lastName || "");
      } else {
        setEmailPromptModalOpen(false);
        setNarratorEmail("");
        setNarratorFirstName("");
        setNarratorLastName("");
        await processSendLinksWithTemplate(selectedTemplate);
      }
    } catch (error) {
      Toast("error", `Failed to save narrator details: ${error.message}`);
      console.error("Error updating custom narrator:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header mainTitle={"Submitted QuickTales"} bgColor="#fff" />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <FilterSection
            handleFilterModalOpen={handleFilterModalOpen}
            statusFilterOptions={statusFilterOptions}
            whoCanListenFilterOptions={whoCanListenFilterOptions}
            modifiedStatusDateFilterOptions={modifiedStatusDateFilterOptions}
            handleDeleteChip={handleDeleteChip}
          />
          <SearchBar
            selected={selected}
            setSelected={setSelected}
            handleUpdateQuickTaleStatus={handleUpdateQuickTaleStatus}
            handleUpdateQuickTaleApprovalStatus={
              handleUpdateQuickTaleApprovalStatus
            }
            handleDeleteQuickTale={handleDeleteModalOpen}
            handleSearch={handleSearchQuickTales}
            handleGetLink={handleGetLink}
            handleSendLink={handleSendLink}
          />
          <AddQuickTalesToOrgTable
            quictalesInOrg={quicktalesWithAreas}
            selected={selected}
            setSelected={setSelected}
          />

        </Grid>
      </Grid>
      <ConfirmationModal
        modalOpen={deleteModal}
        handleClose={handleDeleteModalClose}
        handleClick={handleDeleteQuickTale}
        title={"Are you sure you want to delete the selected QuickTale(s)?"}
      />
      <FilterModalComponent
        modalOpen={filterModal}
        handleClose={handleFilterModalClose}
        resetFilter={resetFilter}
        handleFilter={handleFilterQuickTales}
        data={[
          {
            mainOptions: whoCanListenFilterOptions,
            setMainOptions: setWhoCanListenFilterOptions,
            type: "text",
            title: "Who Can Listen",
            currentOptions: [
              { label: "All", selected: true },
              { label: "public", selected: false },
              { label: "private", selected: false },
              { label: "unlisted", selected: false },
              // { label: "draft", selected: false },
            ],
          },
          {
            mainOptions: statusFilterOptions,
            setMainOptions: setStatusFilterOptions,
            type: "text",
            title: "Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "approved", selected: false },
              { label: "pending", selected: false },
              { label: "denied", selected: false },
              { label: "removed", selected: false },
            ],
          },
          {
            mainOptions: modifiedStatusDateFilterOptions,
            setMainOptions: setModifiedStatusDateFilterOptions,
            type: "date",
            title: "Status Change Date",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Today", selected: false },
              { label: "Past Week", selected: false },
              { label: "Last 30 Days", selected: false },
              { label: "Last 12 Months", selected: false },
              { label: "Enter a Range", selected: false },
            ],
            // title: "Modified Status Date",
          },
        ]}
      />
      <GetLinksModal
        open={getLinkModalOpen}
        onClose={() => setGetLinkModalOpen(false)}
        links={generatedLinks}
      />

      <ConfirmationModal
        open={getLinkTemplateSelectionModalOpen}
        onClose={handleGetLinkTemplateSelectionClose}
        onConfirm={handleGetLinkTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for the narrator page settings."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown
        templates={activeEmailTemplates}
        selectedTemplate={selectedGetLinkTemplate}
        onTemplateChange={(e) => setSelectedGetLinkTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      <ConfirmationModal
        open={templateSelectionModalOpen}
        onClose={handleTemplateSelectionClose}
        onConfirm={handleTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select an email template for these messages."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />
      <ConfirmationModal
        open={emailPromptModalOpen}
        onClose={() => setEmailPromptModalOpen(false)}
        onConfirm={handleEmailPromptContinue}
        title="Narrator Details Required"
        message="Please enter the missing details needed to send the update link."
        textFieldValue={narratorEmail}
        onTextFieldChange={(e) => setNarratorEmail(e.target.value)}
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        narratorName={
          narratorsWithoutEmails[currentNarratorIndex]?.customNarrator?.name ||
          narratorsWithoutEmails[currentNarratorIndex]?.quicktales[0]?.narrator
        }
        firstName={narratorFirstName}
        onFirstNameChange={(e) => setNarratorFirstName(e.target.value)}
        lastName={narratorLastName}
        onLastNameChange={(e) => setNarratorLastName(e.target.value)}
      />
      <ConfirmationModal
        open={showMissingNarratorModal}
        onClose={handleMissingNarratorCancel}
        onConfirm={handleMissingNarratorContinue}
        title="Missing Narrator"
        message={
          <span>
            The QuickTale, <i>{missingNarratorQueue[0]?.title}</i>, does not have a narrator (Told By). Skip <i>{missingNarratorQueue[0]?.title}</i> and continue with the other QuickTales or cancel?
          </span>
        }
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={handlePageLoadingClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AddQuickTalesToOrganizationContainer;
